import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import type { ThemeMode } from '../theme/themeMode';

interface HeaderProps {
  mode: ThemeMode;
  onToggleTheme: () => void;
  onContact: () => void;
}

const Header: React.FC<HeaderProps> = ({ mode, onToggleTheme, onContact }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const menuItems = [
    { label: 'AI 办公', id: 'features', action: () => scrollToSection('features') },
    { label: '工作方式', id: 'workflow', action: () => scrollToSection('workflow') },
    { label: '会议辅助', id: 'meeting', action: () => scrollToSection('meeting') },
    { label: '安全治理', id: 'security', action: () => scrollToSection('security') },
    { label: '下载客户端', id: 'download', action: () => scrollToSection('download') },
  ];

  const closeAndRun = (action: () => void) => {
    setAnchorEl(null);
    action();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: 'rgba(var(--surface-rgb), 0.88)',
        backdropFilter: 'blur(18px)',
        borderBottom: '1px solid rgba(var(--line-rgb), 0.09)',
        color: 'text.primary',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 } }}>
          <Box
            component="a"
            href="#top"
            aria-label="飞舟 Agent 办公首页"
            sx={{
              mr: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              minHeight: 44,
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            <Box
              component="img"
              src="/logo192.png"
              alt=""
              sx={{ width: 32, height: 32, borderRadius: '9px', display: 'block' }}
            />
            <Box>
              <Typography sx={{ fontWeight: 750, fontSize: '1.02rem', lineHeight: 1.15 }}>
                飞舟
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(var(--ink-rgb), 0.52)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: '0.61rem',
                  letterSpacing: '0.12em',
                  lineHeight: 1.3,
                }}
              >
                AGENT OFFICE
              </Typography>
            </Box>
          </Box>

          {isMobile ? (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                aria-label={mode === 'dark' ? '切换到浅色模式' : '切换到深色模式'}
                onClick={onToggleTheme}
                color="inherit"
                sx={{ width: 44, height: 44 }}
              >
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
              <IconButton
                aria-label="打开导航菜单"
                aria-controls="agent-office-menu"
                aria-haspopup="true"
                aria-expanded={Boolean(anchorEl)}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                color="inherit"
                sx={{ width: 44, height: 44 }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="agent-office-menu"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                {menuItems.map((item) => (
                  <MenuItem key={item.id} onClick={() => closeAndRun(item.action)}>
                    {item.label}
                  </MenuItem>
                ))}
                <MenuItem
                  onClick={() => closeAndRun(onContact)}
                >
                  联系团队
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.id}
                    onClick={item.action}
                    sx={{
                      color: 'rgba(var(--ink-rgb), 0.68)',
                      mx: 0.4,
                      px: 1.5,
                      fontSize: '0.88rem',
                      '&:hover': {
                        color: 'rgb(var(--brand-rgb))',
                        backgroundColor: 'rgba(var(--brand-tint-rgb), 0.08)',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
              <IconButton
                aria-label={mode === 'dark' ? '切换到浅色模式' : '切换到深色模式'}
                onClick={onToggleTheme}
                sx={{ ml: 1, color: 'rgba(var(--ink-rgb), 0.68)' }}
              >
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
              <Button
                onClick={onContact}
                variant="contained"
                sx={{
                  ml: 1.5,
                  height: 40,
                  px: 2.6,
                  borderRadius: '999px',
                  backgroundColor: 'rgb(var(--ink-rgb))',
                  color: 'rgb(var(--surface-rgb))',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'rgb(var(--brand-rgb))',
                    boxShadow: 'none',
                  },
                }}
              >
                联系团队
              </Button>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
